// Exportação Excel do Dashboard Comercial Executivo (Analytics Comercial) — mesmo padrão
// já usado em PerformanceDashboard.exportExcel(): uma aba por seção via buildSheet/downloadWorkbook.
import { buildSheet, downloadWorkbook, fileStamp } from './excelExport';

export interface AnalyticsExportInput {
    periodoLabel: string;
    filtrosLabel: string;
    kpis: Array<[string, string | number]>;
    funil: Array<{ etapa: string; leads: number; kwh: number }>;
    statusPanel: Array<{ status: string; qtd: number; kwh: number; pct: number }>;
    canal: Array<{ canal: string; leads: number; aprovados: number; kwhAprovado: number; kwhAssinado: number; conversao: number }>;
    ranking: Array<{ nome: string; leads: number; aprovados: number; assinados: number; kwhAprovado: number; kwhAssinado: number; conversao: number }>;
    motivosPerda: Array<[string, number]>;
    segmento: Array<{ segmento: string; total: number; simulacao: number; aprovado: number; conversao: number; kwh: number }>;
    meta?: { mesLabel: string; meta: number; realizado: number; pct: number; gap: number } | null;
    /** Tabela "Funil por Vendedor": nomes das etapas e, por vendedor, quantidade e kWh
     *  de cada uma, na mesma ordem. A última linha é o total. */
    porVendedor?: { etapas: string[]; linhas: Array<{ nome: string; celulas: Array<{ qtd: number; kwh: number }> }> } | null;
}

export function exportAnalyticsExcel(input: AnalyticsExportInput): void {
    const sheets: { name: string; ws: ReturnType<typeof buildSheet> }[] = [];

    const overview: (string | number)[][] = [
        ['Período', input.periodoLabel],
        ['Filtros ativos', input.filtrosLabel || 'Nenhum'],
        ...input.kpis,
    ];
    sheets.push({ name: 'Visão Geral', ws: buildSheet(['Indicador', 'Valor'], overview, ['txt', 'txt'], { widths: [32, 26] }) });

    sheets.push({
        name: 'Funil',
        ws: buildSheet(
            ['Etapa', 'Leads', 'kWh'],
            input.funil.map(f => [f.etapa, f.leads, Number(f.kwh.toFixed(2))]),
            ['txt', 'num', 'kwh'],
        ),
    });

    sheets.push({
        name: 'Painel de Status',
        ws: buildSheet(
            ['Status', 'Quantidade', 'kWh', '% do funil'],
            input.statusPanel.map(s => [s.status, s.qtd, Number(s.kwh.toFixed(2)), Number(s.pct.toFixed(1))]),
            ['txt', 'num', 'kwh', 'pct'],
        ),
    });

    if (input.canal.length > 0) {
        sheets.push({
            name: 'Canal',
            ws: buildSheet(
                ['Canal', 'Leads', 'Aprovados', 'kWh aprovado', 'kWh assinado', 'Conversão'],
                input.canal.map(c => [c.canal, c.leads, c.aprovados, Number(c.kwhAprovado.toFixed(2)), Number(c.kwhAssinado.toFixed(2)), Number(c.conversao.toFixed(1))]),
                ['txt', 'num', 'num', 'kwh', 'kwh', 'pct'],
            ),
        });
    }

    sheets.push({
        name: 'Ranking',
        ws: buildSheet(
            ['Consultor', 'Leads', 'Aprovados', 'Assinados', 'kWh aprovado', 'kWh assinado', 'Conversão'],
            input.ranking.map(r => [r.nome, r.leads, r.aprovados, r.assinados, Number(r.kwhAprovado.toFixed(2)), Number(r.kwhAssinado.toFixed(2)), Number(r.conversao.toFixed(1))]),
            ['txt', 'num', 'num', 'num', 'kwh', 'kwh', 'pct'],
        ),
    });

    if (input.porVendedor && input.porVendedor.linhas.length > 0) {
        const { etapas, linhas } = input.porVendedor;
        // Duas colunas por etapa (quantidade e kWh), como a tabela da tela.
        const corpo = linhas.slice(0, -1);
        const total = linhas[linhas.length - 1];
        const valores = (l: typeof linhas[number]) => l.celulas.flatMap(c => [c.qtd, Number(c.kwh.toFixed(2))]);
        sheets.push({
            name: 'Por Vendedor',
            ws: buildSheet(
                ['Vendedor', ...etapas.flatMap(e => [e, `${e} (kWh)`])],
                corpo.map(l => [l.nome, ...valores(l)]),
                ['txt', ...etapas.flatMap(() => ['num', 'kwh'] as const)],
                { totals: [total.nome, ...valores(total)], widths: [28, ...etapas.flatMap(() => [12, 16])] },
            ),
        });
    }

    if (input.motivosPerda.length > 0) {
        sheets.push({
            name: 'Motivos de Perda',
            ws: buildSheet(['Motivo', 'Quantidade'], input.motivosPerda.map(([m, q]) => [m, q]), ['txt', 'num']),
        });
    }

    if (input.segmento.length > 0) {
        sheets.push({
            name: 'Segmento',
            ws: buildSheet(
                ['Segmento', 'Leads', 'Simulações', 'Aprovados', 'Conversão', 'kWh'],
                input.segmento.map(s => [s.segmento, s.total, s.simulacao, s.aprovado, Number(s.conversao.toFixed(1)), Number(s.kwh.toFixed(2))]),
                ['txt', 'num', 'num', 'num', 'pct', 'kwh'],
            ),
        });
    }

    if (input.meta) {
        sheets.push({
            name: 'Meta x Realizado',
            ws: buildSheet(
                ['Indicador', 'Valor'],
                [
                    ['Mês', input.meta.mesLabel],
                    ['Meta (kWh)', Number(input.meta.meta.toFixed(2))],
                    ['Realizado (kWh)', Number(input.meta.realizado.toFixed(2))],
                    ['% da meta', Number(input.meta.pct.toFixed(1))],
                    ['Gap (kWh)', Number(input.meta.gap.toFixed(2))],
                ],
                ['txt', 'txt'],
            ),
        });
    }

    downloadWorkbook(sheets, `Analytics_Comercial_${fileStamp()}.xlsx`);
}
